import { useCallback, useLayoutEffect, useRef } from "react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { Eingabe, Knopf } from "@kundeninfo/ui/me-design";
import type { KnopfProps } from "@kundeninfo/ui/me-design";
import "./meHuellen.css";

/*
 * Kundeninfo-Zutaten zu den ME-Elementen (ME-Etappe 4, P4) — die Übergänge aus P3 an EINER
 * Stelle, damit die Kundenakte, die Listen und der Belegdesigner dieselben benutzen und ein
 * Nachzug in me-design (Stand Tarball 511ef0f) sie an einer Stelle entfernen kann:
 *
 *  - `knopfFokus`: me-knopf gibt `focus()` nicht an seinen inneren Knopf weiter (Befund
 *    P3-3). Ohne den Übergang fiele die Fokus-Rückgabe eines Dialogs (me-dialog ruft
 *    `host.focus()`) ins Leere.
 *  - `KiKnopf` mit `name`: me-knopf reicht kein `aria-label` an seinen inneren Knopf (Befund
 *    P3-2). Der Name steht als unsichtbarer Text im Slot, der sichtbare Text ist dann
 *    `aria-hidden`.
 *  - `useInnererPlatzhalter`: me-eingabe kennt keinen Platzhalter (Befund P3-1).
 */

/**
 * Fokus, der einen ABGESCHALTETEN Knopf treffen sollte (siehe `knopfFokus`) — je Host die
 * Optionen und der Zeitpunkt; `KiKnopf` holt ihn nach, sobald er wieder frei ist.
 */
const FOKUS_NACHHOLEN = new WeakMap<HTMLElement, { optionen: FocusOptions | undefined; zeit: number }>();

/** Wie lange ein vorgemerkter Fokus gilt — er gehört zu EINEM Schliessen, nicht zu später. */
const NACHHOLEN_MS = 1000;

/**
 * Übergang (Befund P3-3): leitet `focus()` des me-knopf-Hosts an den inneren `<button>`.
 * Als Ref an `Knopf` zu hängen.
 *
 * Dazu (Befund P4-1): Ein Knopf, der einen Dialog öffnet, ist oft abgeschaltet, SOLANGE der
 * Dialog offen ist („Ändern“, „Neue Notiz“). Beim Schliessen räumt React den Dialog vor dem
 * Freischalten des Knopfes ab (Löschungen vor den Änderungen der Geschwister) — die
 * Fokus-Rückgabe von me-dialog träfe einen noch abgeschalteten Knopf und ginge verloren. Der
 * Fokus wird dann vorgemerkt und von `KiKnopf` nachgeholt, sobald der Knopf frei ist.
 */
export function knopfFokus(host: HTMLElement | null): void {
  if (host === null || Object.prototype.hasOwnProperty.call(host, "focus")) return;
  Object.defineProperty(host, "focus", {
    configurable: true,
    value: (optionen?: FocusOptions) => {
      const knopf = host.shadowRoot?.querySelector<HTMLButtonElement>("button");
      if (knopf === null || knopf === undefined) return;
      if (knopf.disabled) {
        FOKUS_NACHHOLEN.set(host, { optionen, zeit: Date.now() });
        return;
      }
      FOKUS_NACHHOLEN.delete(host);
      knopf.focus(optionen);
    },
  });
}

/** Holt einen vorgemerkten Fokus nach (siehe `knopfFokus`) — nur, wenn er sonst verloren
 *  wäre (nirgends oder auf `body`) und die Vormerkung frisch ist. */
function useFokusNachholen(host: RefObject<HTMLElement | null>, aus: boolean): void {
  useLayoutEffect(() => {
    const element = host.current;
    if (element === null || aus) return;
    const vorgemerkt = FOKUS_NACHHOLEN.get(element);
    if (vorgemerkt === undefined) return;
    FOKUS_NACHHOLEN.delete(element);
    const aktiv = element.ownerDocument.activeElement;
    const verloren = aktiv === null || aktiv === element.ownerDocument.body;
    if (verloren && Date.now() - vorgemerkt.zeit < NACHHOLEN_MS) element.focus(vorgemerkt.optionen);
  }, [host, aus]);
}

/** Das innere Feld eines `me-eingabe` (im Shadow DOM) oder `null`. */
function inneresFeld(host: HTMLElement | null): HTMLInputElement | HTMLTextAreaElement | null {
  return host?.shadowRoot?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
    '[part="feld"]') ?? null;
}

/**
 * Übergang, bis `me-eingabe` einen Platzhalter kennt (Befund P3-1): setzt `placeholder` am
 * inneren Feld nach jedem Zeichnen. Ohne Abhängigkeitsliste, weil das Element das Feld bei
 * `mehrzeilig` austauscht und der Platzhalter dann am neuen Feld fehlen würde.
 */
export function useInnererPlatzhalter(
  host: RefObject<HTMLElement | null>, text: string | undefined,
): void {
  useLayoutEffect(() => {
    const feld = inneresFeld(host.current);
    if (feld === null) return;
    if (text === undefined || text === "") feld.removeAttribute("placeholder");
    else if (feld.placeholder !== text) feld.placeholder = text;
  });
}

export type KiKnopfProps = {
  /** Vorgabe `sekundaer`; `primaer` für den einen Hauptweg eines Bereichs, `gefahr` für
   *  unumkehrbare Aktionen (Löschen), `leise` für Verweise. */
  art?: KnopfProps["art"];
  /** Abgeschaltet (`disabled` am inneren Knopf) — ein Klick bewirkt dann nichts. */
  deaktiviert?: boolean | undefined;
  /**
   * Der zugängliche Name, wo der sichtbare Text allein nicht reicht („Ändern“ an jeder
   * Zeile → „Zahlungskondition für … ändern“). Er steht unsichtbar im Slot, der sichtbare
   * Text wird `aria-hidden` (Übergang zu Befund P3-2).
   */
  name?: string | undefined;
  className?: string | undefined;
  onClick: () => void;
  children: ReactNode;
};

/**
 * Ein Knopf der Anwendung (`me-knopf`) mit den Übergängen für Name und Fokus. Der Klick wird
 * bei `deaktiviert` zusätzlich hier verworfen (der Host selbst kennt kein `disabled`).
 */
export function KiKnopf(p: KiKnopfProps): ReactElement {
  const aus = p.deaktiviert === true;
  const host = useRef<HTMLElement | null>(null);
  const merken = useCallback((element: HTMLElement | null) => {
    host.current = element;
    knopfFokus(element);
  }, []);
  useFokusNachholen(host, aus);
  return (
    <Knopf ref={merken}
      art={p.art ?? "sekundaer"} deaktiviert={aus}
      {...(p.className === undefined ? {} : { className: p.className })}
      onClick={() => { if (!aus) p.onClick(); }}>
      {p.name === undefined ? p.children : (
        <>
          <span className="ki-sr">{p.name}</span>
          <span aria-hidden="true">{p.children}</span>
        </>
      )}
    </Knopf>
  );
}

export type SuchfeldProps = {
  id?: string | undefined;
  /** Der zugängliche Name („Kunden durchsuchen“) — ein Suchfeld hat keine sichtbare
   *  Beschriftung, das Element spiegelt ihn ins innere Feld. */
  name: string;
  platzhalter?: string | undefined;
  wert: string;
  onText: (text: string) => void;
  gesperrt?: boolean | undefined;
  className?: string | undefined;
};

/**
 * Ein Suchfeld (`me-eingabe art="suche"`, Befund 18): Löschkreuz und Suchtastatur des
 * Browsers, ohne sichtbare Beschriftung (die leere Beschriftungszeile des Elements blendet
 * `ki-suchfeld` aus), Platzhalter über den Übergang zu Befund P3-1.
 */
export function Suchfeld(p: SuchfeldProps): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  useInnererPlatzhalter(host, p.platzhalter);
  const klassen = ["ki-suchfeld", p.className ?? ""].filter((k) => k !== "").join(" ");
  return (
    <Eingabe ref={host} art="suche" aria-label={p.name} wert={p.wert} className={klassen}
      {...(p.id === undefined ? {} : { id: p.id })}
      {...(p.gesperrt === true ? { gesperrt: true } : {})}
      onGeaendert={(d) => { if (p.gesperrt !== true) p.onText(d.wert); }} />
  );
}
